<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  options: { type: Array, required: true },
  hasError: { type: Boolean, default: false },
  ariaLabel: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  dropdownMaxHeight: { type: String, default: 'max-h-80' },
  iconSize: { type: Number, default: 24 },
  selectedIconClass: { type: [String, Array, Object], default: '' },
  dropdownDirection: {
    type: String,
    default: 'down',
    validator: value => ['up', 'down'].includes(value),
  },
  dropdownPanelClass: { type: [String, Array, Object], default: '' },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { t } = useI18n();
const open = ref(false);

const selectedOption = computed(() =>
  props.options.find(option => option.key === props.modelValue)
);

const selectedLabel = computed(
  () => selectedOption.value?.label || props.placeholder || ''
);

const triggerStateClass = computed(() => {
  if (props.hasError) {
    return '!border-n-ruby-8 !outline-n-ruby-8 dark:!border-n-ruby-8 dark:!outline-n-ruby-8';
  }
  if (props.disabled) {
    return 'cursor-not-allowed opacity-60 border-n-weak outline-n-weak dark:border-n-strong dark:outline-n-strong';
  }
  if (open.value) {
    return 'border-n-weak outline-n-weak dark:border-n-strong dark:outline-n-strong';
  }
  return 'border-n-weak outline-n-weak hover:border-n-slate-7 hover:outline-n-slate-7 dark:border-n-strong dark:outline-n-strong dark:hover:border-n-slate-7 dark:hover:outline-n-slate-7';
});

const close = () => {
  open.value = false;
};

const toggle = () => {
  if (props.disabled) return;
  open.value = !open.value;
};

const selectOption = key => {
  emit('update:modelValue', key);
  emit('change', key);
  close();
};
</script>

<template>
  <div v-on-click-outside="close" class="relative w-full">
    <button
      type="button"
      class="flex h-[30px] w-full items-center gap-1.5 rounded-lg border border-solid bg-transparent px-3 py-1 text-start outline outline-1 -outline-offset-1 transition-colors"
      :class="triggerStateClass"
      :aria-label="ariaLabel || t('COMBOBOX.PLACEHOLDER')"
      :aria-expanded="open"
      :disabled="disabled"
      @click="toggle"
    >
      <HugeIcon
        v-if="selectedOption?.icon"
        :icon="selectedOption.icon"
        :size="iconSize"
        class="pointer-events-none shrink-0"
        :class="[selectedOption.iconClass, selectedIconClass]"
      />
      <span
        class="h-4 min-w-0 flex-1 truncate font-['Inter'] text-[13px] font-normal leading-4"
        :class="selectedOption ? 'text-n-slate-12' : 'text-n-slate-10'"
      >
        {{ selectedLabel }}
      </span>
      <span
        class="inline-flex h-5 w-5 min-h-5 min-w-5 shrink-0 items-center justify-center text-[24px] leading-none text-n-slate-11"
        :class="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        aria-hidden="true"
      />
    </button>

    <div
      v-if="open"
      class="absolute z-50 w-full overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-lg dark:border-n-strong dark:bg-n-solid-2"
      :class="[
        dropdownDirection === 'up' ? 'bottom-full mb-1' : 'mt-1',
        dropdownPanelClass,
      ]"
      role="listbox"
    >
      <ul class="m-0 list-none overflow-y-auto p-1" :class="dropdownMaxHeight">
        <li v-for="option in options" :key="option.key" role="none">
          <button
            type="button"
            role="option"
            class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            :class="
              option.key === modelValue
                ? 'bg-n-blue-3 dark:bg-n-blue-4/40'
                : 'hover:bg-n-alpha-2 dark:hover:bg-n-alpha-3'
            "
            :aria-selected="option.key === modelValue"
            :disabled="option.disabled"
            @click="selectOption(option.key)"
          >
            <HugeIcon
              v-if="option.icon"
              :icon="option.icon"
              :size="iconSize"
              class="pointer-events-none shrink-0"
              :class="option.iconClass"
            />
            <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
              <span class="font-['Inter']">
                {{ option.label }}
              </span>
            </span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
